import React, { useState, useEffect, ChangeEvent, SyntheticEvent } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Grid, Typography, Link, makeStyles } from '@material-ui/core';
import { getGame } from '../../games/apiQueries';
import { getAiring, getLatestHighlight } from '../../airings/apiQueries';
import EditorForm from '../../common/EditorForm';
import HighlightSelector from './HighlightSelector';
import SourceHighlight from './SourceHighlight';
import HighlightDetailsEditor from './HighlightDetailsEditor';
import DebugPanel from './DebugPanel';
import useHighlightSave from './useHighlightSave';
import { Highlight, Period } from '../../../utils/types';
import { useAlertContext } from '../../../context/alertContext';
import { generateId, comparePeriodStartTime } from './utils';

const getLatestHighlightQueryVariables = (parentId: string, mode: string, type: string): LatestHighlightQueryVariables => {
	if (mode === 'game') {
		return {
			gameId: parentId,
			type: type
		};
	} else if (mode === 'airing') {
		return {
			airingId: parentId,
			type: type
		};
	}

	return {};
};

const useStyles = makeStyles((theme) => ({
	sportLabel: {
		display: 'flex',
		justifyContent: 'flex-end'
	},
	actionLink: {
		color: theme.palette.secondary.main,
		display: 'flex',
		marginBottom: theme.spacing(2)
	},
	selector: {
		marginBottom: theme.spacing(4)
	}
}));

const Composer: React.FC<ComposerProps> = ({ mode = 'game' }) => {
	const { id: parentId }: ComposerParams = useParams();
	const classes = useStyles();
	const { setAlert } = useAlertContext();

	const [initialised, setInitialised] = useState(false);
	const [selectedHighlight, setSelectedHighlight] = useState<Highlight | undefined>(undefined);
	const [newHighlight, setNewHighlight] = useState<Highlight>({
		id: generateId(),
		gameId: parentId,
		airingId: null,
		type: 'LIVE',
		__typename: 'Highlight'
	});
	const [gameId, setGameId] = useState(mode === 'game' ? parentId : 'Unknown');
	const [teams, setTeams] = useState([]);
	const [sport, setSport] = useState('Unknown');
	const [gameStartTime, setGameStartTime] = useState<string | undefined>(undefined);
	const saveHighlight = useHighlightSave(sport === 'F1');
	const [saveDisabled, setSaveDisabled] = useState(true);

	const { data: airingData } = useQuery(getAiring, {
		variables: {
			id: decodeURIComponent(parentId)
		},
		skip: mode !== 'airing'
	});

	const { data: gameData } = useQuery(getGame, {
		variables: {
			id: decodeURIComponent(parentId)
		},
		skip: mode !== 'game'
	});

	const latestHighlightQueryVariables = getLatestHighlightQueryVariables(parentId, mode, 'LIVE');
	const { data: latestHighlightData } = useQuery(getLatestHighlight, {
		variables: latestHighlightQueryVariables,
		fetchPolicy: 'no-cache'
	});

	let editorialPeriodPlayIds: string[] = [];
	if (gameData?.getGame?.editorialHighlight?.periods?.items) {
		editorialPeriodPlayIds = gameData?.getGame?.editorialHighlight?.periods?.items
			.map((period: Period): string => period.playId ?? '')
			.filter((playId: string): boolean => playId !== '');
	} else if (airingData) {
		editorialPeriodPlayIds = airingData?.getAiring?.editorialHighlight?.periods?.items
			.map((period: Period): string => period.playId ?? '')
			.filter((playId: string): boolean => playId !== '');
	}

	const latestDraftHighlightQueryVariables = getLatestHighlightQueryVariables(parentId, mode, 'DRAFT');
	const { data: latestDraftHighlightData } = useQuery(getLatestHighlight, {
		variables: latestDraftHighlightQueryVariables,
		fetchPolicy: 'no-cache'
	});

	const draftPeriods =
		latestDraftHighlightData && latestDraftHighlightData.getLatestHighlight && latestDraftHighlightData.getLatestHighlight.periods
			? latestDraftHighlightData.getLatestHighlight.periods.items
			: undefined;

	const draftPeriodPlayIds: string[] = draftPeriods
		? draftPeriods.map((period: Period): string => period.playId ?? '').filter((playId: string): boolean => playId !== '')
		: [];

	useEffect(() => {
		if (gameData?.getGame?.teams.items) setTeams(gameData?.getGame?.teams.items);
		if (gameData?.getGame?.sport) setSport(gameData?.getGame?.sport);
		if (gameData?.getGame?.startTime) setGameStartTime(gameData?.getGame?.startTime);
	}, [gameData]);

	useEffect(() => {
		if (mode === 'airing') {
			const game = airingData?.getAiring?.game;
			if (game?.teams.items) setTeams(game?.teams.items);
			if (game?.sport) setSport(game?.sport);
			if (game?.startTime) setGameStartTime(game?.startTime);
			if (game?.id) {
				setGameId(game?.id);
				setNewHighlight((highlight) => ({
					...highlight,
					gameId: game?.id,
					airingId: parentId,
					channelId: airingData?.getAiring?.channelId
				}));
			}
		}
	}, [airingData, mode, parentId]);

	useEffect(() => {
		if (latestHighlightData?.getLatestHighlight?.periods?.items?.length > 0) {
			setNewHighlight((currentNewHighlight) => {
				const periods = {
					items: [...latestHighlightData?.getLatestHighlight?.periods?.items].sort(comparePeriodStartTime)
				};
				return {
					...currentNewHighlight,
					id: generateId(),
					periods
				};
			});

			setInitialised(true);
		}
	}, [latestHighlightData]);

	const handleHighlightSelection = (highlight: Highlight): void => {
		setInitialised(true);
		setSelectedHighlight(highlight);
	};

	const handlePeriodRowClick = (event: unknown, data: Period | Period[]): void => {
		const newPeriods = data instanceof Array ? data : [data];
		const sanitisedNewPeriods = newPeriods.map((period) => ({
			...period,
			startPosition: null,
			endPosition: null
		}));

		// filter out duplicate selections by playId
		const currentPeriodPlayIds = (newHighlight.periods?.items || [])
			.map((period: Period): string => period.playId ?? '')
			.filter((playId: string): boolean => playId !== '');

		const filteredNewPeriods = sanitisedNewPeriods.filter((period: Period): boolean => {
			if (!period.playId) return false;
			return !currentPeriodPlayIds.includes(period.playId);
		});

		setNewHighlight((currentNewHighlight) => {
			const periods = {
				items: [...(currentNewHighlight.periods?.items || []), ...filteredNewPeriods].sort(comparePeriodStartTime)
			};
			return {
				...currentNewHighlight,
				id: generateId(),
				periods
			};
		});
		setSaveDisabled(false);
	};

	const handlePeriodDelete = async (oldData: Period): Promise<unknown> => {
		setNewHighlight((currentNewHighlight) => {
			const currentItems = currentNewHighlight.periods?.items;
			if (currentItems?.length) {
				const itemIndex = currentItems.findIndex((item) => JSON.stringify(item) === JSON.stringify(oldData));
				const periods = {
					items: [...currentItems.slice(0, itemIndex), ...currentItems.slice(itemIndex + 1)]
				};
				return {
					...currentNewHighlight,
					id: generateId(),
					periods
				};
			} else {
				return currentNewHighlight;
			}
		});
		setSaveDisabled(false);
		return Promise.resolve();
	};

	const handlePeriodsDelete = async (): Promise<unknown> => {
		setNewHighlight((currentNewHighlight) => {
			const currentItems = currentNewHighlight.periods?.items;
			if (currentItems?.length) {
				const periods = { items: [] };
				return {
					...currentNewHighlight,
					id: generateId(),
					periods
				};
			} else {
				return currentNewHighlight;
			}
		});
		setSaveDisabled(true);
		return Promise.resolve();
	};

	const handlePeriodAdd = async (newData: Period): Promise<unknown> => {
		setNewHighlight((currentNewHighlight) => {
			const currentItems = currentNewHighlight.periods?.items || [];

			const periods = {
				items: [newData, ...currentItems].sort(comparePeriodStartTime)
			};

			return {
				...currentNewHighlight,
				id: generateId(),
				periods
			};
		});
		setSaveDisabled(false);
		return Promise.resolve();
	};

	const handlePeriodUpdate = async (newData: Period, oldData?: Period): Promise<unknown> => {
		setNewHighlight((currentNewHighlight) => {
			const currentItems = currentNewHighlight.periods?.items;
			if (currentItems?.length) {
				const itemIndex = currentItems.findIndex((item) => JSON.stringify(item) === JSON.stringify(oldData));
				const periods = {
					items: [...currentItems.slice(0, itemIndex), newData, ...currentItems.slice(itemIndex + 1)].sort(comparePeriodStartTime)
				};

				return {
					...currentNewHighlight,
					id: generateId(),
					periods
				};
			} else {
				return currentNewHighlight;
			}
		});
		setSaveDisabled(false);
		return Promise.resolve();
	};

	const handleHighlightDetailsChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const { name, value } = event.target;
		const updatedHighlight = {
			...newHighlight,
			id: generateId(),
			[name]: value
		};
		setNewHighlight(updatedHighlight);
	};

	const getChannelFromPeriod = (highlight: Highlight): string => {
		if (highlight.periods?.items?.length) {
			return highlight.periods.items[0].source.channelId ?? '';
		}

		return '';
	};

	const delay = (delayInMSs: number): Promise<void> => {
		return new Promise((res) => setTimeout(res, delayInMSs));
	};

	const handleHighlightCreate = async (event: SyntheticEvent): Promise<unknown> => {
		event.stopPropagation();
		event.preventDefault();

		newHighlight.channelId = newHighlight.channelId || getChannelFromPeriod(newHighlight);

		if (
			// prettier-ignore
			window.confirm(
                `You are creating a new highlight with ${
                    newHighlight.periods?.items?.length
                } clip(s). Are you sure you want to publish this?`
            )
		) {
			setSaveDisabled(true);
			try {
				const { message } = await saveHighlight(newHighlight);
				setAlert(message);
				await delay(3000);
				window.location.reload(); // reloads the page after create highlight successful
			} catch (e) {
				setAlert(`Something went wrong [${e}]`);
			}
			setSaveDisabled(false);
		}
		return Promise.resolve();
	};

	return (
		<EditorForm onSubmit={handleHighlightCreate} submitText='Create Highlight' saveDisabled={saveDisabled}>
			<>
				<Grid spacing={1} container>
					<Grid item xs={4}>
						<Link component={RouterLink} to={`/games/${gameId}`} className={classes.actionLink}>
							Game: {gameId}
						</Link>
					</Grid>
					<Grid item xs={4}>
						{mode === 'airing' && (
							<Link component={RouterLink} to={`/airings/${parentId}`} className={classes.actionLink}>
								Airing: {parentId}
							</Link>
						)}
					</Grid>
					<Grid item xs={4}>
						<Typography variant='body2' className={classes.sportLabel}>
							Sport: {sport}
						</Typography>
					</Grid>
				</Grid>

				<Box className={classes.selector}>
					<HighlightSelector id={parentId} mode={mode} onSelect={handleHighlightSelection} />
				</Box>

				<Grid spacing={1} container>
					<Grid item xs={6}>
						{selectedHighlight && (
							<SourceHighlight
								id={selectedHighlight.id}
								editorialPeriodPlayIds={editorialPeriodPlayIds ?? []}
								sport={sport}
								onPeriodRowPick={handlePeriodRowClick}
							/>
						)}
					</Grid>
					<Grid item xs={6}>
						{initialised && (
							<HighlightDetailsEditor
								gameId={newHighlight.gameId}
								airingId={newHighlight.airingId}
								teams={teams}
								sport={sport}
								gameStartTime={gameStartTime}
								height={640} //in px because video player height is in px
								id={newHighlight.id}
								periods={newHighlight.periods}
								editorialPeriodPlayIds={editorialPeriodPlayIds ?? []}
								draftPeriodPlayIds={draftPeriodPlayIds ?? []}
								type={newHighlight.type}
								onChange={handleHighlightDetailsChange}
								onPeriodAdd={handlePeriodAdd}
								onPeriodUpdate={handlePeriodUpdate}
								onPeriodDelete={handlePeriodDelete}
								onPeriodsDelete={handlePeriodsDelete}
								hidePeriodDetails={true}
							/>
						)}
					</Grid>
				</Grid>
				<DebugPanel highlight={newHighlight} />
			</>
		</EditorForm>
	);
};

type ComposerParams = { id: string };
type ComposerProps = { mode?: 'airing' | 'game' };
type LatestHighlightQueryVariables = {
	airingId?: string;
	gameId?: string;
	type?: string;
};

export default Composer;
